import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // ✅ ADD THIS
import { 
  Settings, 
  Bell, 
  Menu,
  LogOut,
  Sun,
  Moon,
  X, 
  BarChart3, 
  Tag, 
  Database, 
  Store, 
  Zap, 
  Users, 
  Truck, 
  Target, 
  Gift, 
  Shield,
  ChevronDown,
  ChevronRight,
  Package,
  Activity,
  MapPin,
  Clock,
  Maximize2,
  Minimize2,
  TrendingUp,
  Building,
  Monitor,
  QrCode,
  DollarSign,
  Plus,
  Eye,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Search,
  Filter,
  Download,
  ShoppingBag,
  CreditCard,
  MessageSquare,
  List
} from 'lucide-react';

import { useNavigationCounts, formatCount } from '../../hooks/useNavigationCounts';

// ===== LOGOUT SERVICE =====
class LogoutService {
  static logout() {
    try {
      localStorage.removeItem('businessToken');
      localStorage.removeItem('businessData');
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('business');
      localStorage.removeItem('user');
      localStorage.removeItem('userData');
      sessionStorage.clear();
      console.log('User logged out successfully');
      return true;
    } catch (error) {
      console.error('Error during logout:', error);
      return false;
    }
  }
}

// ===== SHARED COMPONENTS =====
interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  className?: string;
}

const Button: React.FC<ButtonProps> = ({ 
  variant = 'primary', 
  size = 'md', 
  children, 
  onClick, 
  disabled, 
  loading, 
  icon,
  className = '' 
}) => {
  const baseStyles = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  const variants = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500',
    outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-blue-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    ghost: 'text-gray-600 hover:bg-gray-100 focus:ring-gray-500'
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    >
      {loading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {icon && !loading && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );
};

const Card: React.FC<{ children: React.ReactNode; className?: string; padding?: boolean }> = ({ 
  children, 
  className = '', 
  padding = true 
}) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${padding ? 'p-6' : ''} ${className}`}>
    {children}
  </div>
);

interface StatsCardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  color?: 'blue' | 'green' | 'red' | 'yellow' | 'purple';
  loading?: boolean;
}

const StatsCard: React.FC<StatsCardProps> = ({ title, value, change, icon, color = 'blue', loading }) => {
  const { t } = useTranslation(); // ✅ ADD THIS
  
  const colors = {
    blue: 'bg-blue-500 text-blue-600 bg-blue-50',
    green: 'bg-green-500 text-green-600 bg-green-50',
    red: 'bg-red-500 text-red-600 bg-red-50',
    yellow: 'bg-yellow-500 text-yellow-600 bg-yellow-50',
    purple: 'bg-purple-500 text-purple-600 bg-purple-50'
  };

  const [bgColor, textColor, lightBg] = colors[color].split(' ');

  if (loading) {
    return (
      <Card>
        <div className="animate-pulse">
          <div className="flex items-center justify-between">
            <div>
              <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-16"></div>
            </div>
            <div className={`w-12 h-12 ${lightBg} rounded-lg`}></div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{typeof value === 'number' ? value.toLocaleString() : value}</p>
          {change !== undefined && (
            <p className={`text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'} flex items-center mt-1`}>
              <TrendingUp className={`w-3 h-3 mr-1 ${change < 0 ? 'rotate-180' : ''}`} />
              {Math.abs(change)}% {t('vs_last_month')}
            </p>
          )}
        </div>
        <div className={`w-12 h-12 ${lightBg} rounded-lg flex items-center justify-center`}>
          <div className={textColor}>
            {icon}
          </div>
        </div>
      </div>
    </Card>
  );
};

// Chart Component
interface ChartProps {
  data: Array<{
    name: string;
    value: number;
    color?: string;
  }>;
  height?: number;
}

const Chart: React.FC<ChartProps> = ({ data, height = 200 }) => {
  const maxValue = Math.max(...data.map(d => d.value));
  
  return (
    <div className="w-full" style={{ height }}>
      <div className="flex items-end justify-between h-full space-x-2 px-4">
        {data.map((item, index) => (
          <div key={index} className="flex flex-col items-center flex-1">
            <div
              className="w-full bg-blue-500 rounded-t-md transition-all duration-500 hover:bg-blue-600 relative group"
              style={{
                height: `${(item.value / maxValue) * 80}%`,
                backgroundColor: item.color || '#3B82F6'
              }}
            >
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                {item.value.toLocaleString()}
              </div>
            </div>
            <span className="text-xs text-gray-600 mt-2 text-center">{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== PAGE COMPONENTS =====

// Business Overview Page
const BusinessOverviewPage: React.FC = () => {
  const { t } = useTranslation(); // ✅ ADD THIS
  const [loading, setLoading] = React.useState(true);
  const [timeRange, setTimeRange] = React.useState('7d');

  React.useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const stats = [
    { title: t('total_revenue'), value: '$125.0K', change: 12.5, icon: <DollarSign className="w-6 h-6" />, color: 'green' as const },
    { title: t('qr_code_scans'), value: 8432, change: 8.2, icon: <Activity className="w-6 h-6" />, color: 'blue' as const },
    { title: t('active_shipments'), value: 89, change: 15.3, icon: <Truck className="w-6 h-6" />, color: 'purple' as const },
    { title: t('products'), value: 6, change: 5.4, icon: <Package className="w-6 h-6" />, color: 'yellow' as const }
  ];

  const chartData = [
    { name: t('mon'), value: 1200, color: '#3B82F6' },
    { name: t('tue'), value: 1800, color: '#3B82F6' },
    { name: t('wed'), value: 1500, color: '#3B82F6' },
    { name: t('thu'), value: 2200, color: '#3B82F6' },
    { name: t('fri'), value: 1900, color: '#3B82F6' },
    { name: t('sat'), value: 2500, color: '#3B82F6' },
    { name: t('sun'), value: 2100, color: '#3B82F6' }
  ];

  const recentActivity = [
    { type: 'scan', message: t('qr_code_scanned'), time: `2 ${t('minutes_ago')}`, icon: <QrCode className="w-4 h-4" />, color: 'blue' },
    { type: 'product', message: t('new_product_added'), time: `1 ${t('hour_ago')}`, icon: <Package className="w-4 h-4" />, color: 'green' },
    { type: 'batch', message: t('batch_generated'), time: `3 ${t('hours_ago')}`, icon: <Tag className="w-4 h-4" />, color: 'purple' },
    { type: 'shipment', message: t('new_batch_created'), time: `5 ${t('hours_ago')}`, icon: <Truck className="w-4 h-4" />, color: 'orange' },
    { type: 'location', message: t('shipment_updated'), time: `6 ${t('hours_ago')}`, icon: <MapPin className="w-4 h-4" />, color: 'blue' }
  ];

  return (
    <div className="p-6 space-y-6 w-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">{t('business_overview')}</h2>
          <p className="text-gray-600">{t('business_overview_desc')}</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="24h">{t('last_24_hours')}</option>
            <option value="7d">{t('last_7_days')}</option>
            <option value="30d">{t('last_30_days')}</option>
            <option value="90d">{t('last_90_days')}</option>
          </select>
          <Button icon={<RefreshCw className="w-4 h-4" />}>{t('refresh')}</Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <StatsCard
            key={index}
            title={stat.title}
            value={stat.value}
            change={stat.change}
            icon={stat.icon}
            color={stat.color}
            loading={loading}
          />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('daily_scan_activity')}</h3>
                <p className="text-sm text-gray-600">{t('daily_scan_activity_desc')}</p>
              </div>
              <Button variant="ghost" icon={<Eye className="w-4 h-4" />} size="sm">{t('view_details')}</Button>
            </div>
            {loading ? (
              <div className="animate-pulse bg-gray-200 rounded h-64"></div>
            ) : (
              <Chart data={chartData} height={300} />
            )}
          </Card>
        </div>

        {/* Recent Activity */}
        <div>
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('recent_activity')}</h3>
              <Button variant="ghost" size="sm">{t('view_all')}</Button>
            </div>
            <div className="space-y-4">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="animate-pulse flex space-x-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))
              ) : (
                recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      activity.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                      activity.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                      activity.color === 'green' ? 'bg-green-100 text-green-600' :
                      activity.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                      'bg-red-100 text-red-600'
                    }`}>
                      {activity.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">{activity.message}</p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Recent Products */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">{t('recent_products')}</h3>
          <Button variant="ghost" size="sm">{t('view_all')}</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Premium Coffee</h4>
            <p className="text-gray-600 text-sm mb-3">High-quality arabica coffee beans</p>
            <div className="text-2xl font-bold text-green-600">$24.99</div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-100">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Organic Tea</h4>
            <p className="text-gray-600 text-sm mb-3">Certified organic green tea</p>
            <div className="text-2xl font-bold text-green-600">$18.5</div>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-100">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Artisan Chocolate</h4>
            <p className="text-gray-600 text-sm mb-3">Handcrafted dark chocolate</p>
            <div className="text-2xl font-bold text-green-600">$12.99</div>
          </div>
        </div>
      </Card>

      {/* Quick Actions */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">{t('quick_actions')}</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button variant="outline" icon={<Plus className="w-4 h-4" />} className="h-20 flex-col justify-center">
            <span className="mt-2 text-center">{t('add_product')}<br/><span className="text-xs text-gray-500">{t('create_new_product')}</span></span>
          </Button>
          <Button variant="outline" icon={<Truck className="w-4 h-4" />} className="h-20 flex-col justify-center bg-green-50 border-green-200 hover:bg-green-100">
            <span className="mt-2 text-center">{t('track_shipment')}<br/><span className="text-xs text-gray-500">{t('supply_chain_tracking')}</span></span>
          </Button>
          <Button variant="outline" icon={<BarChart3 className="w-4 h-4" />} className="h-20 flex-col justify-center bg-purple-50 border-purple-200 hover:bg-purple-100">
            <span className="mt-2 text-center">{t('view_analytics')}<br/><span className="text-xs text-gray-500">{t('performance_data')}</span></span>
          </Button>
          <Button variant="outline" icon={<QrCode className="w-4 h-4" />} className="h-20 flex-col justify-center bg-orange-50 border-orange-200 hover:bg-orange-100">
            <span className="mt-2 text-center">{t('generate_tags')}<br/><span className="text-xs text-gray-500">{t('create_qr_tags')}</span></span>
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Products Page Component
const ProductsPage: React.FC = () => {
  const { t } = useTranslation(); // ✅ ADD THIS
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');

  React.useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const products = [
    { id: 1, name: 'Premium Coffee Beans', sku: 'PCB-001', category: 'Food & Beverage', status: 'active', scans: 1250, tags: 500 },
    { id: 2, name: 'Organic Tea Collection', sku: 'OTC-002', category: 'Food & Beverage', status: 'active', scans: 890, tags: 300 },
    { id: 3, name: 'Artisan Chocolate', sku: 'AC-003', category: 'Food & Beverage', status: 'inactive', scans: 567, tags: 200 },
    { id: 4, name: 'Luxury Watch Series', sku: 'LWS-004', category: 'Accessories', status: 'active', scans: 2100, tags: 150 }
  ];

  return (
    <div className="p-6 space-y-6 w-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">{t('product_management')}</h2>
          <p className="text-gray-600">{t('product_management_desc')}</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />}>{t('add_product')}</Button>
      </div>

      {/* Search */}
      <Card>
        <div className="flex items-center space-x-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('search_products')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            />
          </div>
          <Button variant="ghost" icon={<Filter className="w-4 h-4" />}>{t('filter')}</Button>
          <Button variant="ghost" icon={<Download className="w-4 h-4" />}>{t('export')}</Button>
        </div>
      </Card>

      {/* Products Table */}
      <Card padding={false}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('product')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('category')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('scans')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('tags')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-6 py-4">
                        <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{product.name}</div>
                        <div className="text-sm text-gray-500">SKU: {product.sku}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{product.category}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        product.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {product.status === 'active' ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                        {product.status === 'active' ? t('active') : t('inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{product.scans.toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{product.tags.toLocaleString()}</td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <Button variant="ghost" size="sm">{t('view')}</Button>
                      <Button variant="ghost" size="sm">{t('edit')}</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// Placeholder component for other pages
const PlaceholderPage: React.FC<{ title: string; description: string; icon: React.ReactNode }> = ({ 
  title, 
  description, 
  icon 
}) => {
  const { t } = useTranslation(); // ✅ ADD THIS
  
  return (
    <div className="p-6 w-full">
      <div className="text-center py-12">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <div className="text-gray-400">
            {icon}
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
        <p className="text-gray-600 mb-6">{description}</p>
        <p className="text-sm text-gray-500">{t('under_development')}</p>
      </div>
    </div>
  );
};

// ===== SIDEBAR COMPONENT =====
interface NavigationItem {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number;
  route: string;
  subItems?: NavigationItem[];
  isNew?: boolean;
  hasAlert?: boolean;
}

interface NavigationCategory {
  id: string;
  name: string;
  items: NavigationItem[];
}

interface DashboardSidebarProps {
  activeSection: string;
  onSectionChange: (sectionId: string) => void;
  onClose: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  darkMode?: boolean;
  onToggleDarkMode?: () => void;
  dashboardType: 'business' | 'admin';
  onLogout?: () => void;
  navigationCounts?: any;
  countsLoading?: boolean;
  countsError?: string | null;
  lastUpdated?: Date | null;
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  activeSection,
  onSectionChange,
  onClose,
  isCollapsed = false,
  onToggleCollapse,
  darkMode = false,
  onToggleDarkMode,
  dashboardType = 'business',
  onLogout,
  navigationCounts,
  countsLoading = false,
  countsError,
  lastUpdated
}) => {
  const { t } = useTranslation(); // ✅ ADD THIS
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState<string[]>(['core', 'management', 'business-management', 'tag-operations']);
  const [searchQuery, setSearchQuery] = useState('');

  const handleLogout = () => {
    const confirmed = window.confirm(t('confirm_logout'));
    if (confirmed) {
      LogoutService.logout();
      if (onLogout) {
        onLogout();
      }
      navigate('/business/login');
    }
  };

  const formatBadge = (count: number | undefined, fallback: string): string => {
    if (count === undefined) return fallback;
    return formatCount(count);
  };

  const getNavigationCategories = (): NavigationCategory[] => {
    if (dashboardType === 'admin') {
      return [
        {
          id: 'admin-core',
          name: t('admin_operations'),
          items: [
            { id: 'overview', name: t('admin_overview'), icon: BarChart3, badge: 'Live', route: '/admin/overview' },
            { id: 'system', name: t('system_health'), icon: Monitor, badge: 'Live', hasAlert: true, route: '/admin/system' }
          ]
        },
        {
          id: 'admin-management',
          name: t('platform_management'),
          items: [
            { id: 'businesses', name: t('business_management'), icon: Building, badge: '158', hasAlert: true, route: '/admin/businesses' },
            { id: 'users', name: t('user_management'), icon: Users, badge: '45K', route: '/admin/users' },
            { id: 'security', name: t('security_center'), icon: Shield, hasAlert: true, badge: '23', route: '/admin/security' }
          ]
        }
      ];
    } else {
      return [
        {
          id: 'core',
          name: t('core_operations'),
          items: [
            { 
              id: 'overview', 
              name: t('business_overview'), 
              icon: BarChart3, 
              badge: 'Live', 
              route: '/business/overview' 
            },
            { 
              id: 'analytics', 
              name: t('analytics'), 
              icon: TrendingUp, 
              badge: formatBadge(navigationCounts?.analytics, '12'),
              route: '/business/analytics' 
            }
          ]
        },
        {
          id: 'business-management',
          name: t('business_management'),
          items: [
            { 
              id: 'business', 
              name: t('business_operations'), 
              icon: Building, 
              route: '/business/business' 
            },
            { 
              id: 'products', 
              name: t('product_management'), 
              icon: Package, 
              isNew: true, 
              badge: navigationCounts?.products ? formatBadge(navigationCounts.products, '0') : undefined,
              route: '/business/products' 
            },
            { 
              id: 'stores', 
              name: t('store_management'), 
              icon: Store, 
              badge: formatBadge(navigationCounts?.stores, '24'),
              route: '/business/stores' 
            },
            { 
              id: 'supply-chain', 
              name: t('supply_chain'), 
              icon: Truck, 
              badge: formatBadge(navigationCounts?.shipments, '89'),
              isNew: true, 
              route: '/business/supply-chain' 
            },
            {
              id: 'gs1-settings',
              name: 'GS1 Settings',
              icon: Shield,
              isNew: true,
              route: '/business/gs1-settings'
            },
            {
              id: 'blockchain',
              name: 'Blockchain',
              icon: Zap,
              isNew: true,
              route: '/business/blockchain'
            }
          ]
        },
        {
          id: 'tag-operations',
          name: t('tag_operations'),
          items: [
            { 
              id: 'tags', 
              name: t('tag_management'), 
              icon: Tag, 
              badge: formatBadge(navigationCounts?.tags, '8.9K'),
              route: '/business/tags' 
            },
            { 
              id: 'batches', 
              name: t('batch_management'), 
              icon: Database, 
              badge: formatBadge(navigationCounts?.batches, '47'),
              route: '/business/batches' 
            },
            { 
              id: 'qr-codes', 
              name: t('qr_code_generator'), 
              icon: QrCode, 
              isNew: true, 
              route: '/business/qr-codes' 
            }
          ]
        },
        {
          id: 'marketing',
          name: t('marketing_campaigns'),
          items: [
            { 
              id: 'campaigns', 
              name: t('campaign_management'), 
              icon: Target, 
              badge: navigationCounts?.campaigns ? formatBadge(navigationCounts.campaigns, '0') : undefined,
              route: '/business/campaigns' 
            },
            { 
              id: 'marketing', 
              name: t('marketing_center'), 
              icon: TrendingUp, 
              route: '/business/marketing' 
            },
            { 
              id: 'rewards', 
              name: t('lottery_rewards_system'), 
              icon: Gift, 
              route: '/business/rewards' 
            }
          ]
        },
        {
          id: 'shop-management',
          name: t('shop_management'),
          items: [
            {
              id: 'shop',
              name: t('my_shop'),
              icon: ShoppingBag,
              badge: navigationCounts?.shopOrders ? formatBadge(navigationCounts.shopOrders, '') : 'NEW',
              isNew: !navigationCounts?.shopOrders,
              hasAlert: !!(navigationCounts?.shopOrders && navigationCounts.shopOrders > 0),
              route: '/business/shop'
            }
          ]
        }
      ];
    }
  };

  const navigationCategories = getNavigationCategories();

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleItemClick = (item: NavigationItem) => {
    onSectionChange(item.id);
    navigate(item.route);
  };

  const getBrandColors = () => {
    return dashboardType === 'admin' 
      ? 'from-red-600 to-pink-600' 
      : 'from-blue-600 to-purple-600';
  };

  const getUserColors = () => {
    return dashboardType === 'admin' 
      ? 'from-red-400 to-pink-500' 
      : 'from-blue-400 to-purple-500';
  };

  const renderNavigationItem = (item: NavigationItem): React.ReactElement => {
    const activeColor = dashboardType === 'admin' ? 'bg-red-600' : 'bg-blue-600';
    const isActive = activeSection === item.id;
    
    return (
      <div key={item.id}>
        <button
          onClick={() => handleItemClick(item)}
          className={`group flex items-center justify-between w-full px-4 py-3 text-left transition-all duration-300 relative overflow-hidden rounded-lg mx-2 ${
            isActive 
              ? `${activeColor} text-white shadow-lg` 
              : 'text-gray-300 hover:text-white hover:bg-white/10'
          }`}
        >
          <div className="flex items-center space-x-3 flex-1">
            <div className="relative">
              <item.icon className="w-5 h-5" />
              {item.hasAlert && (
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              )}
            </div>
            
            {!isCollapsed && (
              <>
                <span className="font-medium flex-1">{item.name}</span>
                
                <div className="flex items-center space-x-2">
                  {item.isNew && (
                    <span className="px-2 py-0.5 text-xs bg-green-500 text-white rounded-full font-medium">
                      {t('new')}
                    </span>
                  )}
                  
                  {item.badge && (
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      item.hasAlert 
                        ? 'bg-red-500 text-white animate-pulse' 
                        : isActive
                        ? 'bg-white/20 text-white'
                        : dashboardType === 'admin'
                        ? 'bg-red-500/20 text-red-300'
                        : 'bg-blue-500/20 text-blue-300'
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </button>
      </div>
    );
  };

  const renderCategory = (category: NavigationCategory): React.ReactElement => (
    <div key={category.id} className="mb-6">
      {!isCollapsed && (
        <button
          onClick={() => toggleSection(category.id)}
          className="flex items-center justify-between w-full px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-300 transition-colors"
        >
          <span>{category.name}</span>
          {expandedSections.includes(category.id) ? 
            <ChevronDown className="w-3 h-3" /> : 
            <ChevronRight className="w-3 h-3" />
          }
        </button>
      )}
      
      {(expandedSections.includes(category.id) || isCollapsed) && (
        <div className="space-y-1 mt-2">
          {category.items.map((item: NavigationItem) => renderNavigationItem(item))}
        </div>
      )}
    </div>
  );

  return (
    <div className={`h-screen flex flex-col shadow-2xl transition-all duration-300 bg-gradient-to-b from-slate-800 to-slate-900 ${
      isCollapsed ? 'w-16' : 'w-64'
    }`}>
      {/* Header */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 bg-gradient-to-r ${getBrandColors()} rounded-xl flex items-center justify-center shadow-lg`}>
              <span className="text-white font-bold text-lg">JT</span>
            </div>
            
            {!isCollapsed && (
              <div>
                <h1 className="text-white font-bold text-xl">JepTags</h1>
                <p className="text-gray-300 text-sm">
                  {dashboardType === 'admin' ? t('admin_console') : t('business_center')}
                </p>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                className="text-gray-300 hover:text-white p-1 rounded transition-colors"
                title={isCollapsed ? t('expand_sidebar') : t('collapse_sidebar')}
              >
                {isCollapsed ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </button>
            )}
            
            <button
              onClick={onClose}
              className="lg:hidden text-gray-300 hover:text-white p-1 rounded transition-colors"
              title={t('close_sidebar')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {!isCollapsed && (
          <div className="mt-4">
            <input
              type="text"
              placeholder={t('search_sections')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:bg-white/20 transition-all"
            />
          </div>
        )}
      </div>

      {/* Status indicator */}
      {!isCollapsed && (
        <div className="px-4 py-2">
          <div className={`rounded-lg p-3 border ${
            countsError 
              ? 'bg-yellow-500/20 border-yellow-500/30' 
              : 'bg-green-500/20 border-green-500/30'
          }`}>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                countsError 
                  ? 'bg-yellow-500' 
                  : countsLoading 
                  ? 'bg-blue-500 animate-pulse' 
                  : 'bg-green-500 animate-pulse'
              }`}></div>
              <span className={`text-sm font-medium ${
                countsError ? 'text-yellow-300' : 'text-green-300'
              }`}>
                {countsError 
                  ? t('api_offline') 
                  : countsLoading 
                  ? t('updating_counts') 
                  : dashboardType === 'admin' ? t('system_operational') : t('all_systems_online')
                }
              </span>
            </div>
            {lastUpdated && !countsError && (
              <div className="text-xs text-gray-400 mt-1">
                {t('updated')}: {lastUpdated.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
      )}

      <nav className="flex-1 mt-4 overflow-y-auto">
        {navigationCategories.map(renderCategory)}
      </nav>

      {/* User Profile & Logout */}
      <div className="p-4 border-t border-white/10">
        <div className={`bg-white/10 rounded-xl p-3 backdrop-blur-sm border border-white/20 ${
          isCollapsed ? 'flex items-center justify-center' : ''
        }`}>
          {isCollapsed ? (
            <div className="flex flex-col items-center space-y-2">
              <div className={`w-8 h-8 bg-gradient-to-r ${getUserColors()} rounded-full flex items-center justify-center`}>
                <span className="text-white font-semibold text-xs">
                  {dashboardType === 'admin' ? 'SA' : 'BU'}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="text-gray-300 hover:text-red-400 p-1 rounded transition-colors"
                title={t('sign_out')}
              >
                <LogOut className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 bg-gradient-to-r ${getUserColors()} rounded-full flex items-center justify-center`}>
                  <span className="text-white font-semibold text-xs">
                    {dashboardType === 'admin' ? 'SA' : 'BU'}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium text-sm">
                    {dashboardType === 'admin' ? t('super_admin') : t('business_user')}
                  </p>
                  <p className="text-gray-300 text-xs">
                    {dashboardType === 'admin' ? t('full_access') : t('business_access')}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-1">
                {onToggleDarkMode && (
                  <button
                    onClick={onToggleDarkMode}
                    className="text-gray-300 hover:text-white p-1 rounded transition-colors"
                    title={darkMode ? t('switch_light_mode') : t('switch_dark_mode')}
                  >
                    {darkMode ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
                  </button>
                )}
                
                <button
                  onClick={handleLogout}
                  className="text-gray-300 hover:text-red-400 p-1 rounded transition-colors"
                  title={t('sign_out')}
                >
                  <LogOut className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== MAIN UNIFIED DASHBOARD COMPONENT =====
interface UnifiedDashboardProps {
  dashboardType: 'business' | 'admin';
  children?: React.ReactNode;
}

const UnifiedDashboard: React.FC<UnifiedDashboardProps> = ({ 
  dashboardType = 'business',
  children 
}) => {
  const { t } = useTranslation(); // ✅ ADD THIS
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  const { counts, loading, error, lastUpdated, refresh } = useNavigationCounts();

  const handleLogout = () => {
    LogoutService.logout();
    navigate('/business/login');
  };

  const renderPageContent = () => {
    if (children) {
      return children;
    }

    switch (activeSection) {
      case 'overview':
        return <BusinessOverviewPage />;
      case 'products':
        return <ProductsPage />;
      case 'analytics':
        return <PlaceholderPage 
          title={t('analytics_dashboard')} 
          description={t('analytics_dashboard_desc')}
          icon={<TrendingUp className="w-12 h-12" />}
        />;
      case 'qr-codes':
        return <PlaceholderPage 
          title={t('qr_code_generator')} 
          description={t('qr_code_generator_desc')}
          icon={<QrCode className="w-12 h-12" />}
        />;
      case 'tags':
        return <PlaceholderPage 
          title={t('tag_management')} 
          description={t('tag_management_desc')}
          icon={<Tag className="w-12 h-12" />}
        />;
      case 'campaigns':
        return <PlaceholderPage 
          title={t('campaign_management')} 
          description={t('campaign_management_desc')}
          icon={<Target className="w-12 h-12" />}
        />;
      case 'supply-chain':
        return <PlaceholderPage 
          title={t('supply_chain')} 
          description={t('supply_chain_desc')}
          icon={<Truck className="w-12 h-12" />}
        />;
      case 'shop':
        return <PlaceholderPage
          title={t('my_shop')}
          description={t('my_shop_desc')}
          icon={<ShoppingBag className="w-12 h-12" />}
        />;
      default:
        return <BusinessOverviewPage />;
    }
  };

  return (
    <div className={`min-h-screen flex ${darkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}>
          <div className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm"></div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <DashboardSidebar
          activeSection={activeSection}
          onSectionChange={(section) => {
            setActiveSection(section);
            setSidebarOpen(false);
          }}
          onClose={() => setSidebarOpen(false)}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          darkMode={darkMode}
          onToggleDarkMode={() => setDarkMode(!darkMode)}
          dashboardType={dashboardType}
          onLogout={handleLogout}
          navigationCounts={counts}
          countsLoading={loading}
          countsError={error}
          lastUpdated={lastUpdated}
        />
      </div>

      {/* Main content wrapper */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t('jeptags_business_dashboard')}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {t('manage_business_operations')}
                  {lastUpdated && (
                    <span className="ml-2 text-xs">
                      • {t('updated')} {lastUpdated.toLocaleTimeString()}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Status indicator */}
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  error ? 'bg-red-500' : loading ? 'bg-yellow-500' : 'bg-green-500 animate-pulse'
                }`}></div>
                <span className="text-sm text-gray-600 dark:text-gray-400 hidden sm:inline">
                  {error ? t('api_error') : loading ? t('updating') : t('live')}
                </span>
                <button 
                  onClick={refresh}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded"
                  title={t('refresh_counts')}
                  disabled={loading}
                >
                  <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <button className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
              <div className={`w-8 h-8 bg-gradient-to-r ${
                dashboardType === 'admin' ? 'from-red-500 to-pink-600' : 'from-blue-500 to-purple-600'
              } rounded-full flex items-center justify-center`}>
                <span className="text-white font-semibold text-sm">
                  {dashboardType === 'admin' ? 'SA' : 'BU'}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                title={t('sign_out')}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2">
              <div className="flex items-center justify-between">
                <span className="text-yellow-600 text-sm">
                  ⚠️ {t('api_connection_issue')}
                </span>
                <button 
                  onClick={refresh}
                  className="text-yellow-600 text-sm underline hover:no-underline"
                  disabled={loading}
                >
                  {loading ? t('retrying') : t('retry')}
                </button>
              </div>
            </div>
          )}
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-x-hidden">
          {renderPageContent()}
        </main>
      </div>
    </div>
  );
};

export const BusinessDashboard: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <UnifiedDashboard dashboardType="business" children={children} />
);

export const AdminDashboard: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <UnifiedDashboard dashboardType="admin" children={children} />
);

export default UnifiedDashboard;